// GEO_EXEC_R14 · one list, shared by the sidebar entry and the Distribution page.
//
// Three rules, and they are the whole file:
//  1. **A switched-off feature has no nav item.** `GET .../distribution` 404s with `{"error":
//     "not found"}` while R14 is off — a brand that is not yours 404s with a DIFFERENT body — and
//     the first such answer hides the entry for the rest of the session (sessionStorage, so the
//     client does not pay for a probe on every page load). A nav item that leads to a 404 is worse
//     than no nav item.
//  2. **One request, filtered in the browser.** The month's cap is five pieces, so the whole list
//     is small by construction; refetching it to change a chip would be a spinner where there
//     should be none.
//  3. **A failed probe decides nothing.** Anything other than that one 404 leaves the entry in
//     whatever state it was already in, because "the network hiccuped" is not evidence about
//     whether the feature exists.

import { reactive, watch } from "vue";

import { distributionErrorMessage, isFeatureOff, listDistribution } from "../api/distribution.js";
import { currentBrand } from "./brands.js";

const OFF_KEY = "soa_r14_off";

export const distribution = reactive({
  known: false,
  enabled: false,
  loading: false,
  error: "",
  brandId: null,
  items: [],
  quota: null,
});

let pending = null;
let probing = false;

/** Is this still the request the store is waiting on? A brand switch supersedes the one in flight,
 *  and a superseded request must not write anything: not the rows, not the spinner. */
function current(id) {
  return pending != null && pending.id === id;
}

function rememberedOff() {
  try {
    return sessionStorage.getItem(OFF_KEY) === "1";
  } catch {
    return false; // Storage is optional; without it we simply probe once per page load.
  }
}

function rememberOff() {
  try {
    sessionStorage.setItem(OFF_KEY, "1");
  } catch {
    // Storage is optional.
  }
}

export function switchOff() {
  distribution.known = true;
  distribution.enabled = false;
  distribution.items = [];
  distribution.quota = null;
}

export async function loadDistribution(brandId, { signal } = {}) {
  const id = brandId == null ? null : String(brandId);
  if (!id) {
    distribution.items = [];
    distribution.quota = null;
    distribution.brandId = null;
    return distribution;
  }
  if (rememberedOff()) {
    switchOff();
    return distribution;
  }
  if (pending && pending.id === id) return pending.promise;
  if (id !== distribution.brandId) {
    // A different brand: drop the old brand's rows NOW rather than leave them on screen, labelled
    // with the new brand, until the request lands.
    distribution.items = [];
    distribution.quota = null;
  }
  distribution.loading = true;
  distribution.error = "";
  const promise = listDistribution(id, { signal })
    .then((data) => {
      if (!current(id)) return distribution;
      distribution.known = true;
      distribution.enabled = true;
      distribution.brandId = id;
      distribution.items = Array.isArray(data?.items) ? data.items : [];
      distribution.quota = data?.quota || null;
      return distribution;
    })
    .catch((error) => {
      if (error?.name === "AbortError") return distribution;
      // The switch is a fact about the deployment, not about this brand, so it is believed even
      // from a superseded request. Everything else belongs to a request nobody is waiting for.
      if (!current(id) && !isFeatureOff(error)) return distribution;
      if (isFeatureOff(error)) {
        rememberOff();
        switchOff();
        return distribution;
      }
      distribution.error = distributionErrorMessage(error);
      return distribution;
    })
    .finally(() => {
      // Only the request the store is still waiting on may clear the spinner. A superseded one
      // finishing would otherwise turn off the loading state of the request that replaced it.
      if (!current(id)) return;
      distribution.loading = false;
      pending = null;
    });
  pending = { id, promise };
  return promise;
}

/** Put one row back in place after an edit / a send / a regeneration, so the list never blinks.
 *
 * A regeneration of a piece that is already out in the world returns a NEW row and leaves the old
 * one untouched — its body is what was actually published, and that is the string a citation gets
 * matched against — so an unknown id is added rather than swapped in. */
export function mergeDistributionItem(item) {
  if (!item || item.item_id == null) return;
  const items = distribution.items.slice();
  const at = items.findIndex((row) => row.item_id === item.item_id);
  if (at >= 0) items[at] = { ...items[at], ...item };
  else items.unshift(item);
  distribution.items = items;
}

/** Called once from the app shell. The entry cannot be decided before there is a brand to ask
 *  about, so this waits for one rather than firing a request that would 404 for the wrong reason. */
export function startDistributionProbe() {
  if (probing) return distribution;
  probing = true;
  if (rememberedOff()) {
    switchOff();
    return distribution;
  }
  watch(
    () => currentBrand.value?.id,
    (id) => {
      if (id != null && !distribution.known) loadDistribution(id).catch(() => {});
    },
    { immediate: true },
  );
  return distribution;
}
